/**
 * Display-formula node.
 *
 * KaTeX emits a deep tree of `<span>`s that ProseMirror would flatten to plain
 * text, so the LaTeX is held as a node attribute and KaTeX renders into a
 * node view instead. `data-math` on the incoming HTML is what the parser keys on.
 */
import { mergeAttributes, Node } from '@tiptap/core';
import katex from 'katex';

import { normalizeLatex } from './mathUtils';

export const MathBlock = Node.create({
  name: 'mathBlock',
  group: 'block',
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      'data-math': {
        default: '',
        parseHTML: (element: HTMLElement) => element.getAttribute('data-math') ?? '',
        renderHTML: (attributes: Record<string, unknown>) => ({
          'data-math': attributes['data-math'],
        }),
      },
      'data-math-label': {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-math-label'),
        renderHTML: (attributes: Record<string, unknown>) => {
          const label = attributes['data-math-label'];

          return label ? { 'data-math-label': label } : {};
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-math]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ({ node }) => {
      const latex = String(node.attrs['data-math'] ?? '');
      const label = node.attrs['data-math-label'] as string | null;

      const dom = document.createElement('div');

      dom.setAttribute('data-math', latex);
      dom.setAttribute('data-block-type', 'formula');
      // BlockAttributes registers these on `mathBlock` too, and the renderer
      // puts real values in node.attrs -- but addNodeView() bypasses
      // renderHTML()'s automatic mergeAttributes(), so without this the click
      // handler's `target.closest('[data-block-key]')` never matches a formula.
      for (const name of ['data-block-key', 'data-role', 'data-parent', 'data-label']) {
        const value = node.attrs[name];

        if (value) dom.setAttribute(name, String(value));
      }

      const math = document.createElement('span');

      try {
        katex.render(normalizeLatex(latex), math, {
          displayMode: true,
          throwOnError: false,
        });
      } catch {
        math.textContent = latex;
      }
      dom.appendChild(math);

      if (label) {
        const labelEl = document.createElement('span');

        labelEl.setAttribute('data-part', 'label');
        labelEl.textContent = label;
        dom.appendChild(labelEl);
      }

      return { dom };
    };
  },
});
